import { FC, useState } from "react";
import * as Yup from "yup";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { Translate } from "@/components/Application/Translate";
import { ProcedureNodeFolderItem } from "@/components/Procedures/ProcedureNodeFolders/ProcedureNodeFolderItem";
import { isLink } from "@/components/Procedures/helpers/helperFunctions";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";
import { Spinner } from "@/helpers/components/Spinner";

import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";
import { ProcedureMetaData } from "@/model/Procedure/ProcedureMetaData";

import { SaveProcedureGroup } from "@/service/ProcedureMutations";
import {
  useFetchActiveProcedureGroupFolders,
  useFetchProcedureMetaData,
} from "@/service/ProcedureQueries";

type Props = {
  handleClose: () => void;
  open: boolean;
  isNewLink: boolean;
};

const NewProcedureFolder: FC<Props> = (props) => {
  const { handleClose, open, isNewLink } = props;

  // Keep local state shape (not strictly needed but harmless)
  const [procedureMetaData] = useState<ProcedureMetaData | undefined>(
    new ProcedureMetaData(),
  );

  const { data: procedureMetaDataStore, isLoading } = useFetchProcedureMetaData("?");
  const { groupList } = useFetchActiveProcedureGroupFolders();

  const saveMutation = SaveProcedureGroup();

  // Validation schema (mirrors your training example style)
  const validationSchema = Yup.object().shape({
    Name: Yup.string().trim().required("Name is required"),
    Link: isNewLink
      ? Yup.string()
          .trim()
          .required("Link is required")
          .url("Please enter a valid URL (e.g. https://example.com)")
      : Yup.string().trim().nullable(),
    // ParentId stays optional; root (0) is allowed
  });

  const handleSave = async (values: ProcedureGroup) => {
    try {
      await saveMutation(values);
      handleClose();
      AlertRequest.success();
    } catch (error) {
      console.error(error);
      handleClose();
      AlertRequest.failed();
    }
  };

  if (isLoading || !procedureMetaDataStore) {
    return <Spinner text={"Loading..."} />;
  }

  return (
    <AdminProtected>
      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        aria-labelledby="new-procedure-title"
      >
        {/* Header */}
        <DialogTitle
          id="new-procedure-title"
          sx={{ pr: 6, pb: 0.5, fontWeight: 700 }}
        >
          {isNewLink ? (
            <Translate String="Procedures.NewLink.Header">New Link</Translate>
          ) : (
            <Translate String="Procedures.NewGroup.Header">New group</Translate>
          )}
        </DialogTitle>

        {/* Top-right close */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        {/* Body */}
        <DialogContent dividers sx={{ pt: 1.5 }}>
          {/* Subtitle / helper text */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isNewLink ? (
              <Translate String="Procedures.NewLink.Subtitle">
                Create a shortcut to an external resource and optionally place it in a folder.
              </Translate>
            ) : (
              <Translate String="Procedures.NewGroup.Subtitle">
                Create a new procedure group and optionally choose a parent folder.
              </Translate>
            )}
          </Typography>

          <Formik
            initialValues={{
              Name: "",
              Link: "",
              Id: -1,
              PermisionList: [],
              ParentId: 0,
              SortNumber: 0,
              CanDeletes: true,
              DoDelete: false,
            }}
            validationSchema={validationSchema}
            validateOnMount
            onSubmit={handleSave}
          >
            {({ isValid, dirty, isSubmitting }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Name */}
                  <Grid item xs={12} sm={6}>
                    <CustomInput
                      id="Name"
                      type="text"
                      label="Name"
                      name="Name"
                      translationString="Procedures.Group.Name"
                      placeholder="e.g. Safety Policies"
                      autoFocus
                      fullWidth
                      required
                      onKeyDown={(e: any) => e.stopPropagation()}
                    />
                  </Grid>

                  {/* Link (only when creating a link) */}
                  {isNewLink && (
                    <Grid item xs={12} sm={6}>
                      <CustomInput
                        id="Link"
                        type="text"
                        label="Link"
                        name="Link"
                        translationString="Procedures.Group.Link"
                        placeholder="https://example.com/resource"
                        fullWidth
                        required
                        onKeyDown={(e: any) => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LinkOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}

                  {/* Folder */}
                  <Grid item xs={12} sm={6}>
                    <CustomSelect
                      label="Folder"
                      name="ParentId"
                      id="procedureFolder"
                      translationString="Procedures.Group.Folder"
                      fullWidth
                    >
                      {/* Render folders excluding ones with link-type group numbers */}
                      {procedureMetaDataStore?.Folders?.filter(
                        (folder) => !isLink(folder.GroupNumber, groupList),
                      ).map((folder) => (
                        <ProcedureNodeFolderItem
                          Folder={folder}
                          key={folder.GroupNumber}
                        />
                      ))}
                    </CustomSelect>
                    <Typography variant="caption" color="text.secondary">
                      <Translate String="Procedures.Group.Folder.Help">
                        Choose a parent folder, or leave the default to place it at the root.
                      </Translate>
                    </Typography>
                  </Grid>
                </Grid>

                {/* Actions — right aligned like your example */}
                <DialogActions
                  sx={{
                    px: 0,
                    pt: 2,
                    mt: 0.5,
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<CloseIcon />}
                      onClick={handleClose}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                    >
                      <Translate String="Procedure.Button.Close">Close</Translate>
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="success"
                      type="submit"
                      startIcon={<SaveIcon />}
                      disabled={!isValid || !dirty || isSubmitting}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                    >
                      <Translate String="Procedure.Button.Save">Save</Translate>
                    </Button>
                  </Box>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </AdminProtected>
  );
};

export default NewProcedureFolder;
